import type { CommunityRole, CurrentCommunityMember } from '~/interfaces/community';

export type PostableSpace = { read_only: boolean };

export interface CommunityPermissions {
  // Identity
  isGuest: boolean;
  isReadOnly: boolean;
  isMember: boolean;
  isModerator: boolean;
  isManager: boolean;
  isAdmin: boolean;

  // restrictions
  isBanned: boolean;
  isRestricted: boolean;

  // Participation
  canInteract: boolean;
  canCreatePost: boolean;
  canPostInSpace: (space: PostableSpace | null) => boolean;
  canComment: boolean;
  canReact: boolean;

  // Structure
  canCreateSpace: boolean;

  // Moderation (community-wide)
  canPinAnyPost: boolean;
  canMoveAnyPost: boolean;
  canHideAnyComments: boolean;
  canCloseAnyComments: boolean;
  canRemoveAnyPost: boolean;
  canRemoveAnyComment: boolean;
  canBanMember: boolean;
  canManageAnyGroupChat: boolean;
}

// Every capability key in CommunityPermissions
type RoleGatedPermission =
  | 'canCreateSpace'
  | 'canComment'
  | 'canReact'
  | 'canPinAnyPost'
  | 'canMoveAnyPost'
  | 'canHideAnyComments'
  | 'canCloseAnyComments'
  | 'canRemoveAnyPost'
  | 'canRemoveAnyComment'
  | 'canBanMember'
  | 'canManageAnyGroupChat';

// Each role declares its full permission set explicitly. Duplicate entries
// across roles are intentional — the overlap is a configuration choice, not
// an inheritance relationship, so any role can diverge independently.
const ROLE_PERMISSIONS: Record<CommunityRole, ReadonlySet<RoleGatedPermission>> = {
  manager: new Set([
    'canCreateSpace',
    'canPinAnyPost',
    'canMoveAnyPost',
    'canHideAnyComments',
    'canCloseAnyComments',
    'canRemoveAnyPost',
    'canRemoveAnyComment',
    'canBanMember',
    'canManageAnyGroupChat',
  ]),
  moderator: new Set([
    'canCreateSpace',
    'canPinAnyPost',
    'canMoveAnyPost',
    'canHideAnyComments',
    'canCloseAnyComments',
    'canRemoveAnyPost',
    'canRemoveAnyComment',
  ]),
  admin: new Set([
    'canCreateSpace',
    'canPinAnyPost',
    'canMoveAnyPost',
    'canHideAnyComments',
    'canCloseAnyComments',
    'canRemoveAnyPost',
    'canRemoveAnyComment',
    'canBanMember',
    'canManageAnyGroupChat',
  ]),
};

export const isAuthorProtectedFromBan = (isManager: boolean, authorRoles: readonly CommunityRole[]): boolean =>
  authorRoles.includes('admin') || (isManager && authorRoles.includes('manager'));

export const isAuthorProtectedFromUnban = (isManager: boolean, authorRoles: readonly CommunityRole[]): boolean =>
  isManager && (authorRoles.includes('admin') || authorRoles.includes('manager'));

type GetCommunityPermissionsOptions = {
  isCommunityAccessible?: boolean;
  isPreviewing?: boolean;
};

export const getCommunityPermissions = (
  currentMember: CurrentCommunityMember | null,
  { isCommunityAccessible = true, isPreviewing = false }: GetCommunityPermissionsOptions = {},
): CommunityPermissions => {
  const isGuest = currentMember == null;
  const isBanned = currentMember?.banned === true;
  const isRestricted = isBanned;

  const roles = currentMember?.roles ?? [];
  const isMember = !isGuest;
  // Any member can post. Leaving this here for when
  // we want to add admin-configurable read-only members again.
  const isReadOnly = false;
  const isModerator = !isRestricted && roles.includes('moderator');
  const isManager = !isRestricted && roles.includes('manager');
  const isAdmin = !isRestricted && roles.includes('admin');

  const canInteract = isMember && !isRestricted && isCommunityAccessible && !isPreviewing;
  const hasPermission = (perm: RoleGatedPermission) =>
    canInteract && roles.some((role) => ROLE_PERMISSIONS[role]?.has(perm) ?? false);

  // Posting is channel-dependent: a read-only channel only accepts posts from
  // admins, managers, and moderators. A null space (the aggregated feed) carries no gate.
  const canPostInSpace = (space: PostableSpace | null) => {
    if (!canInteract) return false;
    if (!space?.read_only) return true;

    return isAdmin || isModerator || isManager;
  };

  return {
    isGuest,
    isReadOnly,
    isMember,
    isBanned,
    isRestricted,
    isModerator,
    isManager,
    isAdmin,

    canInteract,
    canCreatePost: canInteract,
    canPostInSpace,
    canComment: canInteract,
    canReact: canInteract,

    canCreateSpace: hasPermission('canCreateSpace'),

    canPinAnyPost: hasPermission('canPinAnyPost'),
    canMoveAnyPost: hasPermission('canMoveAnyPost'),
    canHideAnyComments: hasPermission('canHideAnyComments'),
    canCloseAnyComments: hasPermission('canCloseAnyComments'),
    canRemoveAnyPost: hasPermission('canRemoveAnyPost'),
    canRemoveAnyComment: hasPermission('canRemoveAnyComment'),
    canBanMember: hasPermission('canBanMember'),
    canManageAnyGroupChat: hasPermission('canManageAnyGroupChat'),
  };
};
